import React, { memo, useCallback, useState } from 'react';
import { Delivery, DeliveryStatus } from '../../api/models/deliveries';
import styles from './PastDeliveryCard.module.less';
import { DownOutlined, RedoOutlined, UpOutlined } from '@ant-design/icons';
import { Col, Row, Typography } from 'antd';
import { formatDate } from '../../utils/dates';
import { useTranslation } from 'react-i18next';
import OrderStatus from '../OrderStatus/OrderStatus';
import classnames from 'classnames';
import Paper from '../Paper/Paper';
import { getDistance, getDropOffName, getPassengerName, isCanceled } from '../../utils/deliveries';
import Button from '../Button/Button';
import RaiseEscalationModal from '../RaiseEscalationModal/RaiseEscalationModal';
import WithSkeleton from '../WithSkeleton/WithSkeleton';
import useAnalytika from '../../utils/analytika/reporterAnalytika';
import { raiseEscalationClicked } from '../../utils/analytika/events';

type StatusProps = { status: string; first?: boolean; last?: boolean; bulletClassName?: string };

const Status = (props: StatusProps) => {
  const { status, first, last, bulletClassName } = props;
  return (
    <div className={styles.statusContainer}>
      <div className={styles.bulletContainer}>
        {first ? <div className={styles.hiddenLine} /> : <div className={styles.line} />}
        <div className={classnames(styles.bullet, bulletClassName)} />
        {last ? <div className={styles.hiddenLine} /> : <div className={styles.line} />}
      </div>
      <div>
        <Typography.Text strong>{status}</Typography.Text>
      </div>
    </div>
  );
};

const StatusBar = (props: { status: DeliveryStatus }) => {
  const { status } = props;
  const { t } = useTranslation();
  const isCancelled = isCanceled(status);
  return (
    <div className={styles.statusBar} data-testid="statusBar">
      <Status status={t(`pastDeliveriesPage.deliveryStatus.${DeliveryStatus.Pending}`)} first />
      <Status status={t(`pastDeliveriesPage.deliveryStatus.${DeliveryStatus.Assigned}`)} />
      {isCancelled ? (
        <Status
          bulletClassName={styles.cancelledBullet}
          status={t(`pastDeliveriesPage.deliveryStatus.${DeliveryStatus.Cancelled}`)}
          last
        />
      ) : (
        <>
          <Status status={t(`pastDeliveriesPage.deliveryStatus.${DeliveryStatus.AtPickup}`)} />
          <Status status={t(`pastDeliveriesPage.deliveryStatus.${DeliveryStatus.InProgress}`)} />
          <Status status={t(`pastDeliveriesPage.deliveryStatus.${DeliveryStatus.Delivered}`)} last />
        </>
      )}
    </div>
  );
};

export interface DeliveryCardDetailsProps extends Delivery {
  isTrackingLoading?: boolean;
  isDeliveryDetailsLoading?: boolean;
}

const PastDeliveryCardDetails = (props: DeliveryCardDetailsProps) => {
  const {
    id,
    pickupLocation,
    passenger,
    cashOnDelivery,
    driverNotes,
    driverDetails,
    status,
    bookingId,
    isTrackingLoading,
    isDeliveryDetailsLoading
  } = props;
  const { t } = useTranslation();

  const [showEscalationModal, setShowEscalationModal] = useState(false);
  const onEscalationModalClose = useCallback(() => {
    setShowEscalationModal(false);
  }, [setShowEscalationModal]);

  const { fireEvent } = useAnalytika();

  const handleEscalationModalClick = useCallback(() => {
    fireEvent(raiseEscalationClicked({ delivery_id: id }));
    setShowEscalationModal(true);
  }, [setShowEscalationModal]);

  return (
    <div data-testid="pastDeliveryCardDetails">
      <StatusBar status={status} />
      <Row gutter={[{ md: 24, lg: 24 }, 0]}>
        <Col xs={24} md={24} lg={19} xl={20} xxl={21}>
          <Row gutter={[{ md: 24, lg: 24 }, 0]}>
            <Col xs={24} md={12} lg={8} className={styles.col}>
              <WithSkeleton isLoading={isDeliveryDetailsLoading}>
                <Typography.Text strong>{t('general.pickup')}</Typography.Text>
                <div className={styles.blocked}>
                  <Typography.Text>{pickupLocation?.building}</Typography.Text>
                </div>
                <div className={styles.blocked}>
                  <Typography.Text type="secondary">{pickupLocation?.name}</Typography.Text>
                </div>
              </WithSkeleton>
            </Col>
            <Col xs={24} md={12} lg={8} className={styles.col}>
              <WithSkeleton isLoading={isDeliveryDetailsLoading}>
                <div>
                  <Typography.Text strong>{t('general.dropOff')}</Typography.Text>
                </div>
                {passenger && (
                  <div className={styles.flexSpaceBetween}>
                    <Typography.Text>{getPassengerName(props)}</Typography.Text>
                    <Typography.Text>
                      {passenger.phoneNumber && <a href={`tel:+${passenger.phoneNumber}`}>+{passenger.phoneNumber}</a>}
                    </Typography.Text>
                  </div>
                )}
                <div className={styles.blocked}>
                  <Typography.Text type="secondary">{getDropOffName(props)}</Typography.Text>
                </div>
              </WithSkeleton>
            </Col>
            <Col xs={24} md={12} lg={8} className={classnames(styles.col)}>
              <WithSkeleton isLoading={isTrackingLoading}>
                <div>
                  <Typography.Text strong>{t('pastDeliveriesPage.distance')}</Typography.Text>
                  <div>
                    <Typography.Text type="secondary">{getDistance(props) || '-'}</Typography.Text>
                  </div>
                </div>
              </WithSkeleton>
            </Col>
            <Col xs={24} md={12} lg={8} className={classnames(styles.col)}>
              <WithSkeleton isLoading={isDeliveryDetailsLoading}>
                <Typography.Text strong>{t('pastDeliveriesPage.cashOnDelivery')}</Typography.Text>
                <div>
                  <Typography.Text type="secondary">{cashOnDelivery ? cashOnDelivery : '-'}</Typography.Text>
                </div>{' '}
              </WithSkeleton>
            </Col>
            <Col xs={24} md={12} lg={8} className={classnames(styles.col)}>
              <WithSkeleton isLoading={isDeliveryDetailsLoading}>
                <Typography.Text strong>{t('pastDeliveriesPage.deliveryDetails')}</Typography.Text>
                <div>
                  <Typography.Text type="secondary">{driverNotes ? driverNotes : '-'}</Typography.Text>
                </div>
              </WithSkeleton>
            </Col>
            <Col xs={24} md={12} lg={8} className={classnames(styles.col)}>
              <WithSkeleton isLoading={isTrackingLoading}>
                <Typography.Text strong>{t('pastDeliveriesPage.captainDetails')}</Typography.Text>
                <div>
                  <Typography.Text type="secondary">{driverDetails?.driverName || '-'}</Typography.Text>
                </div>
                <div>
                  <Typography.Text type="secondary">{driverDetails?.driverNumber || '-'}</Typography.Text>
                </div>
              </WithSkeleton>
            </Col>
          </Row>
        </Col>
        <Col xs={24} md={24} lg={5} xl={4} xxl={3} className={classnames(styles.col, styles.escalationButtonContainer)}>
          <Button
            block
            className={styles.escalationButton}
            data-testid={'raiseEscalationButton'}
            onClick={handleEscalationModalClick}
          >
            {t('pastDeliveriesPage.raiseEscalation')}
          </Button>
        </Col>
      </Row>
      {showEscalationModal && (
        <RaiseEscalationModal referenceId={bookingId} visible={showEscalationModal} onClose={onEscalationModalClose} />
      )}
    </div>
  );
};

export type PastDeliveryCardProps = {
  dateFormat: string;
  timeFormat: string;
  isExpanded?: boolean;
  handleExpandClick: (bookingId: string) => void;
  onRecreate?: (bookingId: string) => Promise<any>;
} & DeliveryCardDetailsProps;

const PastDeliveryCard = (props: PastDeliveryCardProps) => {
  const { t } = useTranslation();
  const {
    id,
    orderId,
    pickUpTime,
    status,
    dateFormat,
    timeFormat,
    handleExpandClick,
    isExpanded,
    onRecreate,
    bookingId,
    isCNF
  } = props;
  const [recreating, setRecreating] = useState(false);

  const onExpandClick = () => {
    const selectedId = isExpanded ? '' : id;
    handleExpandClick(selectedId.toString());
  };

  const onRecreateClick = useCallback(() => {
    if (onRecreate) {
      setRecreating(true);
      onRecreate(bookingId).finally(() => {
        setRecreating(false);
      });
    }
  }, [onRecreate, setRecreating]);

  return (
    <Paper className={styles.card} data-testid="pastDeliveryCard">
      <Row align="middle">
        <Col xs={14} md={6} xl={5} className={styles.deliveryIdContainer}>
          <Typography.Text strong>{t('deliveries.deliveryWithId', { id })}</Typography.Text>
        </Col>
        <Col xs={24} md={5} xl={5} className={styles.orderIdContainer}>
          <Typography.Text type="secondary">
            {orderId ? t('pastDeliveriesPage.orderId', { orderId }) : '-'}
          </Typography.Text>
        </Col>
        <Col xs={19} md={5} xl={9} className={styles.dateContainer}>
          <Col xs={12} md={24} xl={12}>
            {pickUpTime && <Typography.Text type="secondary">{formatDate(pickUpTime, dateFormat)}</Typography.Text>}
          </Col>
          <Col xs={12} md={24} xl={12}>
            {pickUpTime && <Typography.Text type="secondary">{formatDate(pickUpTime, timeFormat)}</Typography.Text>}
          </Col>
        </Col>

        <Col xs={10} md={4} xl={3} className={styles.orderStatusContainer}>
          <OrderStatus status={status} />
        </Col>
        <Col xs={5} md={4} xl={2} className={styles.expandIconContainer}>
          {onRecreate && isCNF && (
            <Button
              data-testid="recreateBtn"
              type="link"
              icon={<RedoOutlined />}
              className={styles.recreateIcon}
              title={t('pastDeliveriesPage.recreateTitle')}
              disabled={recreating}
              onClick={onRecreateClick}
            />
          )}
          {isExpanded ? (
            <UpOutlined data-testid="PastDeliveryCard-expandBtn" onClick={onExpandClick} />
          ) : (
            <DownOutlined data-testid="PastDeliveryCard-expandBtn" onClick={onExpandClick} />
          )}
        </Col>
      </Row>
      {isExpanded && <PastDeliveryCardDetails {...props} />}
    </Paper>
  );
};

PastDeliveryCard.defaultProps = {
  dateFormat: 'd MMM yyyy',
  timeFormat: 'p'
};

export default memo(PastDeliveryCard);
